import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildHtmlFilename,
  countLines,
  downloadHtmlFile,
  isInlineFigureHtml,
  isPreviewableHtml,
  normalizeHtmlForMatch,
} from '../../utils/htmlPreview';
import { trackChatUxEvent } from '../../utils/eventTracker';
import { writeTextToClipboard } from '../../utils/answerClipboard';
import { useHtmlPreviewContext } from '../chat/HtmlPreviewContext';
import InlineHtmlFigure from './InlineHtmlFigure';

// 답변을 쓰는 중인지 — 덜 써진 HTML을 내려받거나 실행하지 않게 한다.
export const MarkdownStreamingContext = createContext(false);

const COLLAPSE_MIN_LINES = 40;

const CodeBlock = ({ language = 'code', code = '' }) => {
  const streaming = useContext(MarkdownStreamingContext);
  const htmlPreview = useHtmlPreviewContext();
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const resetTimerRef = useRef(null);
  const displayLanguage = language.replace(/^language-/, '') || 'code';
  const displayCode = useMemo(
    () => String(code).replace(/\r\n?/g, '\n'),
    [code]
  );
  const previewable = useMemo(
    () => isPreviewableHtml(displayLanguage, displayCode),
    [displayLanguage, displayCode]
  );
  const lineCount = useMemo(() => countLines(displayCode), [displayCode]);
  const canPreview = previewable && Boolean(htmlPreview);
  const inlineFigure = useMemo(
    () => canPreview && Boolean(htmlPreview.inlineFigures) && !streaming && isInlineFigureHtml(displayCode),
    [canPreview, htmlPreview?.inlineFigures, streaming, displayCode] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const collapsible = previewable && (inlineFigure || lineCount >= COLLAPSE_MIN_LINES);
  const collapsed = collapsible && !expanded;
  const normalizedCode = useMemo(
    () => (canPreview && htmlPreview.activeCodeNormalized ? normalizeHtmlForMatch(displayCode) : null),
    [canPreview, htmlPreview?.activeCodeNormalized, displayCode] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const isShowing = Boolean(normalizedCode) && normalizedCode === htmlPreview?.activeCodeNormalized;

  useEffect(() => () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
  }, []);

  const scheduleReset = (callback, delay) => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(callback, delay);
  };

  const handleCopy = async () => {
    try {
      await writeTextToClipboard(displayCode);
      setCopyFailed(false);
      setCopied(true);
      scheduleReset(() => setCopied(false), 1600);
      trackChatUxEvent('code_copy', { entry: 'codeblock', stage: displayLanguage });
    } catch (error) {
      console.error('코드 복사 실패:', error);
      setCopyFailed(true);
      scheduleReset(() => setCopyFailed(false), 2000);
    }
  };

  const handleDownload = () => {
    downloadHtmlFile(displayCode, buildHtmlFilename(displayCode));
    trackChatUxEvent('html_download', { entry: 'codeblock' });
  };

  const codeBlock = (
    <div className={`em-codeblock ${collapsed ? 'is-collapsed' : ''} ${inlineFigure ? 'is-figure-source' : ''}`}>
      <div className="em-codeblock-header">
        <span className="em-codeblock-language">
          {displayLanguage}
          {previewable && lineCount > 0 && (
            <span className="em-codeblock-lines">{lineCount}줄</span>
          )}
        </span>
        <div className="em-codeblock-actions">
          {canPreview && !inlineFigure && (
            <button
              type="button"
              className="em-codeblock-preview"
              onClick={() => htmlPreview.openPreview(displayCode)}
              disabled={streaming}
              aria-label="오른쪽에서 실행해 보기"
              title={streaming ? '답변이 끝나면 실행해 볼 수 있어요' : undefined}
            >
              {isShowing ? '보는 중' : '미리보기'}
              {htmlPreview.isNewFeature && !streaming && (
                <span className="em-codeblock-preview-new" aria-hidden="true">NEW</span>
              )}
            </button>
          )}
          {previewable && (
            <button
              type="button"
              className="em-codeblock-copy"
              onClick={handleDownload}
              disabled={streaming}
              aria-label="HTML 파일로 받기"
              title={streaming ? '답변이 끝나면 받을 수 있어요' : undefined}
            >
              .html 받기
            </button>
          )}
          <button
            type="button"
            className={`em-codeblock-copy ${copied ? 'is-copied' : ''} ${copyFailed ? 'is-failed' : ''}`}
            onClick={handleCopy}
            aria-label="코드 복사"
          >
            {copyFailed ? '복사 실패' : copied ? '복사됨' : '복사'}
          </button>
        </div>
      </div>
      <pre className="em-codeblock-pre">
        <code className={`em-codeblock-code language-${displayLanguage}`}>{displayCode}</code>
      </pre>
      {collapsible && (
        <button
          type="button"
          className="em-codeblock-expand"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          {expanded ? '코드 접기' : `코드 펼치기 (${lineCount}줄)`}
        </button>
      )}
    </div>
  );

  if (!inlineFigure) return codeBlock;

  return (
    <>
      <InlineHtmlFigure
        code={displayCode}
        onExpand={() => htmlPreview.openPreview(displayCode, { entry: 'inline' })}
      />
      {codeBlock}
    </>
  );
};

export { writeTextToClipboard };

export default CodeBlock;
